/**
 * QualGear disclosure engine — anchored popover (desktop) / bottom sheet
 * (mobile), per the LOCKED trust-badges spec (trust-badges-round3.md M4):
 * never a new tab, Esc + outside-click + Close-button dismiss, focus moves
 * in on open and returns to the trigger on close, only one open at a time.
 *
 * Fully generic and content-free: any trigger with
 * [data-qg-disclosure-trigger="KEY"] opens the content of the matching
 * <template data-qg-disclosure-id="KEY">, cloned verbatim. Used by
 * qg-trust-badges.liquid and qg-reseller-partner-block.liquid; reusable by
 * any future badge/disclosure pattern without new JS.
 *
 * Reuses the theme's global trapFocus/removeTrapFocus (global.js, loaded
 * theme-wide) rather than reimplementing a focus trap.
 */
(function () {
  if (window.qgDisclosureInit) return;
  window.qgDisclosureInit = true;

  var MOBILE_QUERY = '(max-width: 640px)';
  var open = null; // { trigger, layers: [scrim, panel], mode }
  var panelIdCounter = 0; // D-A-002 (punch-1): unique ids for dynamically-built panels

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function focusables(el) {
    return Array.prototype.slice.call(
      el.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    );
  }

  function closeDisclosure() {
    if (!open) return;
    var state = open;
    open = null;
    document.removeEventListener('keydown', state.onKey, true);
    if (window.removeTrapFocus) {
      window.removeTrapFocus(state.trigger);
    }
    state.layers.forEach(function (node) {
      if (node && node.parentNode) node.parentNode.removeChild(node);
    });
    state.trigger.setAttribute('aria-expanded', 'false');
    state.trigger.removeAttribute('aria-controls');
    if (typeof state.trigger.focus === 'function') state.trigger.focus();
  }

  function buildPanelContent(templateEl, closeId) {
    var frag = templateEl.content.cloneNode(true);
    var wrap = document.createElement('div');
    wrap.appendChild(frag);
    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'qg-disclosure__close';
    closeBtn.setAttribute('data-qg-disclosure-close', '');
    closeBtn.setAttribute('aria-label', (window.QG_I18N && window.QG_I18N.close) || 'Close');
    closeBtn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
    var head = wrap.querySelector('.qg-disclosure__head');
    if (head) head.appendChild(closeBtn);
    else wrap.insertBefore(closeBtn, wrap.firstChild);
    return wrap;
  }

  function bindDialog(panel, trigger) {
    panel.setAttribute('role', 'dialog');
    var titleEl = panel.querySelector('[data-qg-disclosure-title]');
    var leadEl = panel.querySelector('[data-qg-disclosure-lead]');
    if (titleEl) {
      if (!titleEl.id) titleEl.id = 'qg-disc-title-' + Math.random().toString(36).slice(2, 8);
      panel.setAttribute('aria-labelledby', titleEl.id);
    }
    if (leadEl) {
      if (!leadEl.id) leadEl.id = 'qg-disc-lead-' + Math.random().toString(36).slice(2, 8);
      panel.setAttribute('aria-describedby', leadEl.id);
    }
    var closeBtn = panel.querySelector('[data-qg-disclosure-close]');
    if (closeBtn) closeBtn.addEventListener('click', closeDisclosure);

    var onKey = function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDisclosure();
        return;
      }
      if (event.key === 'Tab') {
        /* RS1010-AXE-D06 (FIX-1, rs-2026-10-10). Every Tab inside the open
           dialog is moved here, never by the browser. A native Tab move runs
           a scroll-into-view that honours the sticky-header scroll-padding
           (qg-sections.css, RS0930-NAV-D01); a popover placed ABOVE its badge
           has its Close button inside that band, so the browser scrolled the
           page, the scroll-close listener below saw > 8 px and closed the
           popover on the first Tab. Focus moves with preventScroll: the
           popover is clamped into the viewport and the sheet is fixed, so
           nothing inside either ever needs a page scroll. The first Tab from
           the panel itself (focus lands there on open) goes to the first
           control; Shift+Tab from it goes to the last. */
        var items = focusables(panel);
        event.preventDefault();
        if (!items.length) return;
        var idx = items.indexOf(document.activeElement);
        var next;
        if (event.shiftKey) {
          next = idx <= 0 ? items[items.length - 1] : items[idx - 1];
        } else {
          next = idx === -1 || idx === items.length - 1 ? items[0] : items[idx + 1];
        }
        try {
          next.focus({ preventScroll: true });
        } catch (e) {
          next.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey, true);

    // RT-FIXA-D07 (layer 1 — remove the cause). Move focus into the dialog
    // OURSELVES, without scrolling the document.
    //
    // global.js `trapFocus(container, elementToFocus)` focuses elementToFocus
    // inside a `setTimeout(..., 300)`. That late `focus()` scrolls the page
    // whenever the freshly positioned popover is even a few pixels out of view,
    // and the capture-phase scroll listener at the bottom of this file read that
    // browser-initiated scroll as a user scroll and closed the popover ~300ms
    // after it opened — so it could not be opened at all on desktop.
    //
    // Passing `null` as elementToFocus installs the trap handlers only: a
    // default parameter applies to `undefined`, never to `null`, so trapFocus'
    // `if (elementToFocus)` branch is skipped and nothing focuses late. Focus
    // still lands on the panel (same target as before, now immediately).
    try {
      panel.focus({ preventScroll: true });
    } catch (e) {
      panel.focus();
    }
    if (window.trapFocus) {
      window.trapFocus(panel, null);
    } else if (!panel.contains(document.activeElement)) {
      var items = focusables(panel);
      (items[0] || panel).focus();
    }
    return onKey;
  }

  function openPopover(trigger, templateEl) {
    var scrim = document.createElement('div');
    scrim.className = 'qg-disclosure__scrim qg-disclosure__scrim--popover';
    var panel = document.createElement('div');
    panel.className = 'qg-disclosure qg-disclosure--popover';
    panel.id = 'qg-disc-panel-' + (++panelIdCounter);
    trigger.setAttribute('aria-controls', panel.id);
    panel.setAttribute('tabindex', '-1');
    var arrow = document.createElement('span');
    arrow.className = 'qg-disclosure__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    panel.appendChild(arrow);
    panel.appendChild(buildPanelContent(templateEl));
    document.body.appendChild(scrim);
    document.body.appendChild(panel);

    var rect = trigger.getBoundingClientRect();
    var scrollX = window.pageXOffset;
    var scrollY = window.pageYOffset;
    var viewportWidth = document.documentElement.clientWidth;
    var panelWidth = panel.offsetWidth;
    var panelHeight = panel.offsetHeight;
    var left = rect.left + scrollX + rect.width / 2 - panelWidth / 2;
    left = Math.max(scrollX + 12, Math.min(left, scrollX + viewportWidth - panelWidth - 12));
    var below = rect.bottom + panelHeight + 14 < window.innerHeight;
    var top = below ? rect.bottom + scrollY + 10 : rect.top + scrollY - panelHeight - 10;
    if (!below) panel.classList.add('qg-disclosure--above');
    // RT-FIXA-D07. Keep the panel inside the viewport vertically, mirroring the
    // horizontal clamp above. The browser used to do this by scrolling the page
    // when focus moved in; focus no longer scrolls (see bindDialog), so the
    // panel places itself in view instead. Only bites when it would otherwise
    // be clipped, and is skipped when the panel is taller than the viewport.
    var minTop = scrollY + 12;
    var maxTop = scrollY + window.innerHeight - panelHeight - 12;
    if (maxTop > minTop) top = Math.max(minTop, Math.min(top, maxTop));
    panel.style.left = left + 'px';
    panel.style.top = top + 'px';
    var arrowX = rect.left + scrollX + rect.width / 2 - left - 7;
    arrow.style.left = Math.max(14, Math.min(arrowX, panelWidth - 28)) + 'px';

    var onKey = bindDialog(panel, trigger);
    scrim.addEventListener('mousedown', closeDisclosure);
    trigger.setAttribute('aria-expanded', 'true');
    open = {
      trigger: trigger,
      layers: [scrim, panel],
      mode: 'popover',
      onKey: onKey,
      scrollY: window.pageYOffset // RT-FIXA-D07: baseline for the scroll-close test
    };
  }

  function openSheet(trigger, templateEl) {
    var scrim = document.createElement('div');
    scrim.className = 'qg-disclosure__scrim qg-disclosure__scrim--sheet';
    var panel = document.createElement('div');
    panel.className = 'qg-disclosure qg-disclosure--sheet';
    panel.id = 'qg-disc-panel-' + (++panelIdCounter);
    trigger.setAttribute('aria-controls', panel.id);
    panel.setAttribute('tabindex', '-1');
    panel.setAttribute('aria-modal', 'true');
    var grip = document.createElement('span');
    grip.className = 'qg-disclosure__grip';
    grip.setAttribute('aria-hidden', 'true');
    panel.appendChild(grip);
    panel.appendChild(buildPanelContent(templateEl));
    document.body.appendChild(scrim);
    document.body.appendChild(panel);

    var onKey = bindDialog(panel, trigger);
    scrim.addEventListener('mousedown', closeDisclosure);
    trigger.setAttribute('aria-expanded', 'true');
    open = {
      trigger: trigger,
      layers: [scrim, panel],
      mode: 'sheet',
      onKey: onKey,
      scrollY: window.pageYOffset
    };
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-qg-disclosure-trigger]');
    if (!trigger) return;
    event.preventDefault();
    var key = trigger.getAttribute('data-qg-disclosure-trigger');
    var templateEl = document.querySelector('template[data-qg-disclosure-id="' + key + '"]');
    if (!templateEl) return;

    if (open && open.trigger === trigger) {
      closeDisclosure();
      return;
    }
    closeDisclosure();

    if (isMobile()) {
      openSheet(trigger, templateEl);
    } else {
      openPopover(trigger, templateEl);
    }
  });

  // D-F-009 fix: robust outside-click close independent of the scrim's own
  // hit-testing (mousedown on .qg-disclosure__scrim above stays as a second
  // path). Mirrors the mega-menu's proven click+closest() outside-click
  // pattern (qg-header.js): ignore clicks on a disclosure trigger (the
  // dedicated trigger handler above already opens/closes/toggles those) and
  // clicks inside the open panel; anything else outside closes it.
  document.addEventListener('click', function (event) {
    if (!open) return;
    if (event.target.closest('[data-qg-disclosure-trigger]')) return;
    var panel = open.layers[1];
    if (panel && panel.contains(event.target)) return;
    closeDisclosure();
  });

  window.addEventListener('resize', function () {
    if (open) closeDisclosure();
  });
  // A genuine user scroll still closes an anchored popover — the panel is
  // positioned against the trigger in document coordinates, so following the
  // page is deliberate. RT-FIXA-D07 (layer 2 — defence in depth): compare
  // against the document offset recorded when the popover opened and ignore
  // sub-threshold movement. Two things this protects against: a
  // browser-initiated nudge (focus, caret reveal, anchor) closing a popover the
  // instant it opens, and — because this listener is in the capture phase and
  // therefore also sees scroll events from any descendant element — a scroll
  // inside the panel's own content closing it. The threshold is deliberately
  // small: one wheel notch, one arrow key or one trackpad flick clears it.
  var SCROLL_CLOSE_PX = 8;
  window.addEventListener(
    'scroll',
    function () {
      if (!open || open.mode !== 'popover') return;
      if (Math.abs(window.pageYOffset - open.scrollY) <= SCROLL_CLOSE_PX) return;
      closeDisclosure();
    },
    true
  );
})();
